import { useTrackedQuery } from "@/hooks/apollo-insights-hooks";
import * as z from "zod";

import { ChevronDownIcon } from "@/components/icons";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from "@/components/ui/form";
import { createUserSchema, updateUserSchema } from "../users.const";

import { Input } from "@/components/ui/input";
import { Skeleton } from "@/components/ui/skeleton";
import { isNil } from "@/helper/utils";
import { cn } from "@/lib/utils";
import { QUERY_CITY_LIST } from "@/services/web/api-cities.const";
import { Auth0Roles } from "@/store/auth/auth.type";
import { zodResolver } from "@hookform/resolvers/zod";
import { Loader2 } from "lucide-react";
import { useMemo, useState } from "react";
import { useForm } from "react-hook-form";
import { toast } from "sonner";
import { useCreateUser, useUpdateUser } from "../hooks/use-users";
import { ICreateNewUser } from "../users.type";
import { CloseEyeIcon } from "@/components/icons/close-eye-icon";
import { EyeIcon } from "@/components/icons/eye-icon";

type CreateFormValues = z.infer<typeof createUserSchema>;
type UpdateFormValues = z.infer<typeof updateUserSchema>;

interface UserModalProps {
  user: ICreateNewUser;
  onClose: () => void;
  onSuccess?: () => void;
}

export const CreateNewUserModal = ({ user, onClose, onSuccess }: UserModalProps) => {
  const { createUser, loading: userCreationInProgress, error: createUserError } = useCreateUser();
  const { updateUser, loading: updatedInprogress, error: updateUserError } = useUpdateUser(user.id ?? "");
  const [open, setOpen] = useState(false);
  const isUpdate = Boolean(user.id);

  const [sortCitiesAZ, setSortCitiesAZ] = useState(true);
  const [showPassword, setShowPassword] = useState(false);

  const form = useForm<UpdateFormValues | CreateFormValues>({
    resolver: zodResolver(isUpdate ? updateUserSchema : createUserSchema),
    defaultValues: { ...user },
    mode: "all",
  });

  const watchRoles = form.watch("roles");
  const showCityList = watchRoles.includes("Viewer");

  const { data: cityData, loading: isCitiesLoading } = useTrackedQuery(QUERY_CITY_LIST, {
    skip: !showCityList,
  });

  const cityList = useMemo(() => {
    if (isNil(cityData)) return [];
    return cityData.cities.map((city) => ({
      value: city.id,
      label: city.name,
    }));
  }, [cityData]);

  const sortedCityList = useMemo(() => {
    if (!cityList) return [];
    return [...cityList].sort((a, b) => {
      return sortCitiesAZ
        ? a.label.localeCompare(b.label, undefined, { sensitivity: "base" })
        : b.label.localeCompare(a.label, undefined, { sensitivity: "base" });
    });
  }, [cityList, sortCitiesAZ]);

  const saveUser = async (data: UpdateFormValues | CreateFormValues) => {
    const newUser = {
      firstName: data.firstName,
      lastName: data.lastName,
      roles: data.roles,
      email: data.email,
      password: data.password,
      cities: data.cities,
    };

    try {
      if (isUpdate) {
        await updateUser({ ...newUser, id: user.id ?? "" });
        toast.success("User has been updated successfully", {
          style: { color: "#228B22" },
        });
      } else {
        await createUser(newUser);
        toast.success("User has been created successfully", {
          style: { color: "#228B22" },
        });
      }
      onSuccess?.();
    } catch {
      if (createUserError) {
        toast.error(createUserError.message || "Failed to create user");
      } else if (updateUserError) {
        toast.error(updateUserError.message || "Failed to update user");
      } else {
        toast.error("An error occurred");
      }
    }
    onClose();
    form.reset();
  };

  const buttonLabel = user.id ? "Update User" : "Create User";

  return (
    <Dialog open={true} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-[425px]">
        <DialogHeader>
          <DialogTitle>{user.id ? "Edit " : "Add "} a user</DialogTitle>
          <DialogDescription>User information</DialogDescription>
        </DialogHeader>
        <Form {...form}>
          <form onSubmit={form.handleSubmit(saveUser)} className="space-y-4">
            <FormField
              control={form.control}
              name="firstName"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>First Name</FormLabel>
                  <FormControl>
                    <Input {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="lastName"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Last Name</FormLabel>
                  <FormControl>
                    <Input {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="email"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Email</FormLabel>
                  <FormControl>
                    <Input type="email" disabled={!!user.id} {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            {/*
              Ajout d'un bouton oeil pour afficher/cacher le mot de passe
            */}
            {(() => {
              return (
                <FormField
                  control={form.control}
                  name="password"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Password</FormLabel>
                      <FormControl>
                        <div className="relative">
                          <Input type={showPassword ? "text" : "password"} {...field} />
                          <button
                            type="button"
                            tabIndex={-1}
                            className="absolute inset-y-0 right-0 flex items-center px-2 text-gray-500"
                            onClick={() => setShowPassword((v) => !v)}
                            aria-label={showPassword ? "Hide password" : "Show password"}
                          >
                            {showPassword ? <CloseEyeIcon className="w-4 h-4 " /> : <EyeIcon className="w-4 h-4 " />}
                          </button>
                        </div>
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              );
            })()}
            <FormField
              control={form.control}
              name="roles"
              render={() => (
                <FormItem>
                  <FormLabel>Roles</FormLabel>
                  <div className="space-y-2">
                    {Object.values(Auth0Roles).map((role, idx) => (
                      <FormField
                        key={role}
                        control={form.control}
                        name="roles"
                        render={({ field }) => (
                          <FormItem className="flex items-center space-x-2">
                            <FormControl>
                              <div className="flex flex-row items-center space-x-2">
                                <Checkbox
                                  checked={field.value?.includes(role)}
                                  onCheckedChange={(checked) => {
                                    const updatedRoles = checked
                                      ? field.value && [...field.value, role]
                                      : field.value?.filter((r) => r !== role);
                                    field.onChange(updatedRoles);
                                  }}
                                  id={`${role}-${idx}`}
                                />
                                <label
                                  htmlFor={`${role}-${idx}`}
                                  className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70"
                                >
                                  {role}
                                </label>
                              </div>
                            </FormControl>
                          </FormItem>
                        )}
                      />
                    ))}
                  </div>
                  <FormMessage />
                </FormItem>
              )}
            />
            {showCityList && (
              <FormField
                control={form.control}
                name="cities"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>City</FormLabel>
                    <DropdownMenu open={open} onOpenChange={setOpen}>
                      <DropdownMenuTrigger asChild>
                        <Button
                          variant="default"
                          role="combobox"
                          aria-expanded={open}
                          className={cn("w-full justify-between", !field.value?.length && "text-muted-foreground")}
                        >
                          <span>
                            {Array.isArray(field.value) && field.value.length > 0
                              ? `${field.value.length} cities selected`
                              : "Select cities"}
                          </span>
                          <ChevronDownIcon className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                        </Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent className="w-[200px] max-h-[300px] overflow-y-auto" align="start">
                        <div className="flex items-center justify-end px-2 py-1">
                          <Button
                            variant={"outline"}
                            type="button"
                            className="ml-auto text-xs font-medium border border-input bg-background hover:bg-accent hover:text-accent-foreground rounded-md h-7 flex items-center"
                            onClick={() => {
                              // Toggle sort order in local state
                              setSortCitiesAZ((prev) => !prev);
                            }}
                          >
                            <span className="hidden lg:inline"> {sortCitiesAZ ? "Sort (Z-A)" : "Sort (A-Z)"}</span>
                          </Button>
                        </div>
                        {isCitiesLoading ? (
                          <Skeleton />
                        ) : (
                          sortedCityList.map((city) => (
                            <DropdownMenuItem
                              key={city.value}
                              onSelect={(e) => {
                                e.preventDefault();
                                const values = field.value || [];
                                const newValues = values.includes(city.value)
                                  ? values.filter((value) => value !== city.value)
                                  : [...values, city.value];
                                field.onChange(newValues);
                              }}
                              className="flex items-center space-x-2"
                            >
                              <Checkbox checked={field.value?.includes(city.value)} className="mr-2" />
                              <span>{city.label}</span>
                            </DropdownMenuItem>
                          ))
                        )}
                      </DropdownMenuContent>
                    </DropdownMenu>
                    <FormMessage />
                  </FormItem>
                )}
              />
            )}
            <DialogFooter className="sm:justify-between">
              <Button
                type="button"
                variant="outline"
                onClick={() => {
                  onClose();
                  form.reset();
                }}
              >
                Cancel
              </Button>
              <Button
                type="submit"
                className={`bg-primary text-primary-foreground hover:bg-primary/90 ${userCreationInProgress || (updatedInprogress && "cursor-not-allowed opacity-55")}`}
              >
                {userCreationInProgress || updatedInprogress ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    Loading...
                  </>
                ) : (
                  buttonLabel
                )}
              </Button>
            </DialogFooter>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
};
